import defer from 'lodash/defer';
import isNil from 'lodash/isNil';
import {
  useRef,
  useCallback,
} from 'react';

export default function useRecordingVisualizer() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameId = useRef<number>(0);
  const containerRef = useRef<HTMLCanvasElement>(null);

  const drawRef = useRef<() => void>(null);
  const isPausedRef = useRef(true);

  const frameCounterRef = useRef(0);
  const initializeCanvas = useCallback(() => {
    // initialize the canvas with a dummy "silent" waveform and draw it on canvas
    defer(() => {
      const canvas = containerRef.current;
      const canvasCtx = canvas?.getContext('2d');

      if (!isNil(canvasCtx) && !isNil(canvas)) {
        const barWidthInFrames = 2;
        const gapWidthInFrames = 1;
        const totalCycle = barWidthInFrames + gapWidthInFrames;
        const dummyBarHeight = 1; // The height of the silent line in pixels
        const y = (canvas.height - dummyBarHeight) / 2; // Center the line vertically

        // First, fill the entire background
        canvasCtx.fillStyle = '#EFF6FF';
        canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

        // Then, draw the dummy bars on top
        canvasCtx.fillStyle = '#93C5FD';
        for (let x = 0; x < canvas.width; x += 1) {
        // Use the modulo operator to replicate the bar/gap pattern
          if ((x % totalCycle) < barWidthInFrames) {
            canvasCtx.fillRect(x, y, 1, dummyBarHeight);
          }
        }
        canvasCtx.drawImage(canvas, 0, 0);
      }
    });
  }, []);

  const start = useCallback((stream: MediaStream) => {
    const canvas = containerRef.current;
    if (!stream || !canvas) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
    }

    const analyser = analyserRef.current!;
    analyser.fftSize = 2048;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyser);

    const canvasCtx = canvas.getContext('2d')!;

    // =======================================================
    // NEW: Pre-fill the canvas with a dummy "silent" waveform
    // =======================================================
    const barWidthInFrames = 2;
    const gapWidthInFrames = 1;
    const totalCycle = barWidthInFrames + gapWidthInFrames;
    const dummyBarHeight = 1; // The height of the silent line in pixels
    const y = (canvas.height - dummyBarHeight) / 2; // Center the line vertically

    // First, fill the entire background
    canvasCtx.fillStyle = '#EFF6FF';
    canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

    // Then, draw the dummy bars on top
    canvasCtx.fillStyle = '#93C5FD';
    for (let x = 0; x < canvas.width; x += 1) {
      // Use the modulo operator to replicate the bar/gap pattern
      if ((x % totalCycle) < barWidthInFrames) {
        canvasCtx.fillRect(x, y, 1, dummyBarHeight);
      }
    }
    // =======================================================

    const draw = () => {
      if (isPausedRef.current) return;
      animationFrameId.current = requestAnimationFrame(draw);

      const scrollSpeed = 1;
      // This will now scroll the pre-filled image we just created
      canvasCtx.drawImage(canvas, -scrollSpeed, 0);

      frameCounterRef.current = (frameCounterRef.current + 1) % totalCycle;

      canvasCtx.fillStyle = '#EFF6FF';
      canvasCtx.fillRect(canvas.width - scrollSpeed, 0, scrollSpeed, canvas.height);

      if (frameCounterRef.current < barWidthInFrames) {
        analyser.getByteTimeDomainData(dataArray);

        let peak = 0;
        for (let i = 0; i < bufferLength; i += 1) {
          const amplitude = Math.abs(dataArray[i] - 128);
          if (amplitude > peak) {
            peak = amplitude;
          }
        }

        const barHeight = (peak / 128) * canvas.height * 2;
        const maxHeight = canvas.height;
        const cappedBarHeight = Math.min(barHeight, maxHeight) * 0.8;
        const barY = (canvas.height - cappedBarHeight) / 2;

        canvasCtx.fillStyle = '#93C5FD';
        canvasCtx.fillRect(canvas.width - scrollSpeed, barY, scrollSpeed, cappedBarHeight);
      }
    };

    drawRef.current = draw;
    isPausedRef.current = false;
    draw();
  }, []);

  const pause = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const resume = useCallback(() => {
    isPausedRef.current = false;
    if (drawRef.current) {
      drawRef.current();
    }
  }, []);

  const stop = useCallback(() => {
    isPausedRef.current = true;
    cancelAnimationFrame(animationFrameId.current);
    const canvas = containerRef.current;
    if (canvas) {
      const canvasCtx = canvas.getContext('2d')!;
      canvasCtx.fillStyle = '#EFF6FF';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  return { start, stop, pause, resume, containerRef, initializeCanvas };
}
